import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { TransactionAuthorizationContext } from 'features/authorization/types/authorizationGuide';
import { AuthorizationChannel } from 'features/authorization/constants/authorizationChannels';
import { MerchantNumberRule } from 'features/authorization/constants/merchantNumbers';
import {
	formatMerchantNumberDisplay,
	getRecommendedMerchantRule,
} from 'features/authorization/helpers/authorizationGuide';
import { formatAmountForCopy, formatTransactionAmount } from 'features/transaction-details/helpers';
import { canShowCardSensitiveInfo } from 'features/transaction-details/mappers';
import { getCardInfo } from 'features/transactions/services';
import { CopyableFieldRow } from './CopyableFieldRow';

interface AuthorizationCopyPanelProps {
	transactionContext: TransactionAuthorizationContext;
	carrierCode: string;
	amountToAuthorize: string;
	channel: AuthorizationChannel;
	onCopied?: (message: string) => void;
	isAdminOrOperator: boolean;
	/** Las reglas de la base o la tabla fija de respaldo (`useMerchantRules`); sin ellas, la tabla fija. */
	rules?: readonly MerchantNumberRule[];
}

export const AuthorizationCopyPanel = ({
	transactionContext,
	carrierCode,
	amountToAuthorize,
	channel,
	onCopied,
	isAdminOrOperator,
	rules,
}: AuthorizationCopyPanelProps): JSX.Element | null => {
	const [cardFullInfo, setCardFullInfo] = useState<{ cardNumber: number; cardSecurityCode: number } | null>(null);
	const [loadingCard, setLoadingCard] = useState(false);
	const [revealed, setRevealed] = useState(false);
	const cardNumberCopyButtonRef = useRef<HTMLButtonElement>(null);

	if (!carrierCode || channel === 'no_cards') {
		return null;
	}

	const recommended = getRecommendedMerchantRule(
		carrierCode,
		transactionContext.cardStamp,
		transactionContext.fee,
		rules,
	);
	const merchantDisplay =
		recommended && recommended.merchantNumber !== 'GDS_ONLY' && recommended.merchantNumber !== 'NOT_ACCEPTED'
			? recommended.merchantNumber
			: recommended
			? formatMerchantNumberDisplay(recommended.merchantNumber)
			: '—';

	const amountNumeric =
		amountToAuthorize && Number(amountToAuthorize) > 0
			? Number(amountToAuthorize)
			: transactionContext.pendingAuthorization;

	const displayAmount = formatTransactionAmount(amountNumeric, transactionContext.coinCode);
	const copyAmount = formatAmountForCopy(amountNumeric);

	const canReveal = isAdminOrOperator && canShowCardSensitiveInfo(transactionContext.flagShowCardInfo);

	const focusCardNumberCopy = (): void => {
		requestAnimationFrame(() => {
			cardNumberCopyButtonRef.current?.focus();
		});
	};

	const handleRevealCard = async (): Promise<void> => {
		if (cardFullInfo) {
			setRevealed(true);
			focusCardNumberCopy();
			return;
		}
		setLoadingCard(true);
		try {
			const response = await getCardInfo(transactionContext.transactionId);
			setCardFullInfo({
				cardNumber: response.data.cardNumber,
				cardSecurityCode: response.data.cardSecurityCode,
			});
			setRevealed(true);
		} catch {
			onCopied?.('Error al obtener datos de tarjeta');
		} finally {
			setLoadingCard(false);
		}
	};

	useEffect(() => {
		if (revealed && cardFullInfo) {
			focusCardNumberCopy();
		}
	}, [revealed, cardFullInfo]);

	const cardNumberValue =
		revealed && cardFullInfo ? cardFullInfo.cardNumber.toString() : transactionContext.cardNumberFormatted;

	const cvcValue =
		revealed && cardFullInfo ? cardFullInfo.cardSecurityCode.toString() : transactionContext.cardSecurityCodeFormatted;

	return (
		<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 1 }}>
			<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
				Datos para ingresar en {channel === 'call_center' ? 'call center' : 'Fiserv'}
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
				Copiá cada campo con el botón 📋
			</Typography>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				{channel !== 'gds' && <CopyableFieldRow label="Cód. de comercio" value={merchantDisplay} onCopied={onCopied} />}
				<CopyableFieldRow label="Monto" value={displayAmount} copyValue={copyAmount} onCopied={onCopied} />
				<CopyableFieldRow label="Cuotas" value={String(transactionContext.fee)} onCopied={onCopied} />
				{transactionContext.document && (
					<CopyableFieldRow label="Documento titular" value={transactionContext.document} onCopied={onCopied} />
				)}
				<CopyableFieldRow label="Nombre titular" value={transactionContext.cardholder} onCopied={onCopied} />
				{canReveal && !revealed && (
					<Button
						size="small"
						variant="outlined"
						startIcon={loadingCard ? <CircularProgress size={14} /> : <VisibilityIcon />}
						onClick={handleRevealCard}
						disabled={loadingCard}
						fullWidth
						sx={{ justifyContent: 'flex-start' }}
					>
						{loadingCard ? 'Cargando...' : 'Revelar nº tarjeta y CVC para copiar'}
					</Button>
				)}
				<CopyableFieldRow
					label="Nº tarjeta"
					value={cardNumberValue}
					onCopied={onCopied}
					copyButtonRef={cardNumberCopyButtonRef}
				/>
				<CopyableFieldRow label="Vencimiento" value={transactionContext.cardExpiration} onCopied={onCopied} />
				<CopyableFieldRow label="CVC" value={cvcValue} onCopied={onCopied} />
			</Box>
		</Paper>
	);
};
